// frontend/src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { login, loginAsInstructor } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Session expiration time in minutes
  const SESSION_EXPIRATION = 60; // 1 hour
  
  // Check if user is logged in on mount
  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        // Check for instructor token first
        const instructorToken = localStorage.getItem('instructor_token');
        if (instructorToken) {
          // Verify the token is still valid
          try {
            // Make a request to a protected endpoint that requires instructor auth
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8008'}/instructor/questions?limit=1`, {
              headers: {
                'Authorization': `Bearer ${instructorToken}`
              }
            });
            
            if (response.ok) {
              const instructorName = localStorage.getItem('instructor_name') || 'Instructor';
              setUser({ role: 'instructor', name: instructorName });
              setLoading(false);
              return; // Exit early if instructor auth succeeds
            } else {
              // Invalid token, clear it
              localStorage.removeItem('instructor_token');
              localStorage.removeItem('instructor_name');
            }
          } catch (error) {
            console.error("Error verifying instructor token:", error);
            localStorage.removeItem('instructor_token');
            localStorage.removeItem('instructor_name');
          }
        }
        
        // Check for student authentication
        const sharedPassword = getCookie('shared_password');
        if (sharedPassword) {
          // Verify the shared password is still valid
          try {
            // Make a request to a protected endpoint that requires student auth
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8008'}/chat/verify`, {
              method: 'GET',
              headers: {
                'Authorization': sharedPassword
              },
              credentials: 'include'
            });
            
            if (response.ok) {
              setUser({ role: 'student' });
            } else {
              // Invalid password, clear the cookie
              document.cookie = 'shared_password=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
              setUser(null);
            }
          } catch (error) {
            console.error("Error verifying shared password:", error);
            document.cookie = 'shared_password=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };
    
    checkLoginStatus();
  }, []);
  
  const loginStudent = async (password) => {
    try {
      await login(password);
      setUser({ role: 'student' });
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };
  
  const loginInstructor = async (credentials) => {
    try {
      const data = await loginAsInstructor(credentials);
      setUser({ 
        role: 'instructor',
        name: data.name
      });
      return true;
    } catch (error) {
      console.error('Instructor login error:', error);
      return false;
    }
  };
  
  const logout = () => {
    // Clear cookies and localStorage
    document.cookie = 'shared_password=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    localStorage.removeItem('instructor_token');
    localStorage.removeItem('instructor_name');
    localStorage.removeItem('session_expiry');
    setUser(null);
  };
  
  const value = {
    user,
    loading,
    loginStudent,
    loginInstructor,
    logout
  };
  
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Helper function to get a cookie value
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop().split(';').shift();
    // URL-decode the cookie value
    return decodeURIComponent(cookieValue);
  }
  return null;
}